import { Link, useLoaderData } from "react-router";
import { PetiteAnnonce } from "../lib/interfaces/petite-annonce.interface";
import { useForm } from "react-hook-form";
import MyFormField from "../components/ui/MyFormField";
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { ArrowLeftIcon, Trash, TriangleAlert } from "lucide-react";
import Title from "../components/ui/Title";
import Box from "../components/ui/Box";
import { useEffect, useMemo, useState } from "react";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { CreatePetiteAnnonceData } from "../lib/interfaces/create-petite-annonce-data.interface";
import { formatPrix } from "../lib/helpers/prix_helpers";
import dayjs from "dayjs";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreatePetiteAnnonceDto, createPetiteAnnonceSchema } from "../lib/schemas/petites-annonces-schema";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { toast } from "sonner";
import { TypePays } from "../lib/enums/type-pays.enum";
import { ksPetitesAnnoncesQK, ksUserQK } from "../lib/query-keys";
import { Checkbox } from "../components/ui/checkbox";
import { useCalculerPrixPetiteAnnonce, useCreatePetiteAnnonce } from "../lib/hooks/usePetitesAnnonces";
import clsx from "clsx";

interface PublierPetiteAnnoncePageLoaderData extends CreatePetiteAnnonceData {
  annonceADupliquer?: PetiteAnnonce | null;
}

type LigneSupplementaireName = "ligne5" | "ligne6" | "ligne7" | "ligne8" | "ligne9" | "ligne10";

interface LigneSupplementaireProps {
  ligneNum: number;
  nbLignes: number;
  setNbLignes: (value: number | ((prev: number) => number)) => void;
  form: ReturnType<typeof useForm<CreatePetiteAnnonceDto>>;
  categorie?: { isGratuit?: boolean } | null;
  onLigneComplete: (ligneNum: number, longueur: number) => void;
}

function LigneSupplementaire({
  ligneNum,
  nbLignes,
  setNbLignes,
  form,
  categorie,
  onLigneComplete,
}: LigneSupplementaireProps) {
  if (nbLignes < ligneNum) {
    return null;
  }

  const ligneName = `ligne${ligneNum}` as LigneSupplementaireName;
  const nextLigneName: LigneSupplementaireName | null = ligneNum < 10 ? (`ligne${ligneNum + 1}` as LigneSupplementaireName) : null;

  const handleDelete = () => {
    // Si une ligne suivante existe et a du contenu, le copier dans la ligne actuelle
    if (nextLigneName) {
      const nextLigneValue = form.watch(nextLigneName);
      const trimmedNextLigne = nextLigneValue?.trim() || "";
      form.setValue(ligneName, trimmedNextLigne);
      if (trimmedNextLigne) {
        form.setValue(nextLigneName, "");
      }
    } else {
      // Pour la dernière ligne (ligne10), on vide simplement
      form.setValue(ligneName, "");
    }
    setNbLignes(nbLignes - 1);
  };

  const ligneValue = form.watch(ligneName);

  return (
    <div className='max-w-[450px] flex flex-row gap-3'>
      <MyFormField
        control={form.control}
        label={`Ligne ${ligneNum}`}
        disabled={categorie?.isGratuit}
        name={ligneName}
        className='flex-1 w-full md:w-[450px]'
        maxLength={27}
        description={`${ligneValue?.length || 0}/27 caractères`}
        onInput={(event) => onLigneComplete(ligneNum, event.currentTarget.value.length)}
      />
      <button
        type='button'
        className='text-red-400 hover:text-red-300 transition-colors duration-200'
        onClick={handleDelete}
      >
        <Trash />
      </button>
    </div>
  );
}

export default function PublierPetiteAnnoncePage() {
  const { annonceADupliquer, user, categories, tarif, moisDeParution } =
    useLoaderData() as PublierPetiteAnnoncePageLoaderData;
  const queryClient = useQueryClient();
  const [parent] = useAutoAnimate();
  const [nbLignes, setNbLignes] = useState(4);
  const form = useForm<CreatePetiteAnnonceDto>({
    resolver: zodResolver(createPetiteAnnonceSchema),
    defaultValues: {
      personneMorale: user.personneMorale || false,
      raisonSociale: user.raisonSociale || "",
      categorieId: annonceADupliquer?.categorieId || "",
      nom: user.nom || "",
      prenom: user.prenom || "",
      adresse1: user.adresse1 || "",
      adresse2: user.adresse2 || "",
      codePostal: user.codePostal || "",
      ville: user.ville || "",
      telephone: user.telephone || "",
      ligne1: annonceADupliquer?.ligne1 || "",
      ligne2: annonceADupliquer?.ligne2 || "",
      ligne3: annonceADupliquer?.ligne3 || "",
      ligne4: annonceADupliquer?.ligne4 || "",
      ligne5: annonceADupliquer?.ligne5 || "",
      ligne6: annonceADupliquer?.ligne6 || "",
      ligne7: annonceADupliquer?.ligne7 || "",
      ligne8: annonceADupliquer?.ligne8 || "",
      ligne9: annonceADupliquer?.ligne9 || "",
      ligne10: annonceADupliquer?.ligne10 || "",
      tramage: annonceADupliquer?.tramage || false,
      mois: [] as string[],
      typePays: user.typePays || TypePays.FRANCE,
      pays: user.pays || "FRANCE",
      siret: user.siret || "",
      noTva: user.noTva || "",
    },
  });

  const categorie = useMemo(() => {
    return categories.find((c) => c.id === form.watch("categorieId"));
  }, [form.watch("categorieId")]);

  useEffect(() => {
    if (categorie?.isGratuit) {
      form.setValue("tramage", false);
      // Vider toutes les lignes supplémentaires (5 à 10)
      const lignesSupplementaires: LigneSupplementaireName[] = ["ligne5", "ligne6", "ligne7", "ligne8", "ligne9", "ligne10"];
      lignesSupplementaires.forEach((ligne) => {
        form.setValue(ligne, "");
      });
      setNbLignes(4);
    }
  }, [categorie]);

  const { data: prixData, isFetching: isCalculatingPrix } = useCalculerPrixPetiteAnnonce(
    {
      categorieId: form.watch("categorieId"),
      nbLignes: +nbLignes,
      tramage: form.watch("tramage"),
      nbParutions: form.watch("mois").length,
    },
    {
      enabled: !!nbLignes && !!form.watch("categorieId") && form.watch("mois").length > 0,
    }
  );

  const createMutation = useCreatePetiteAnnonce();

  const handleLigneComplete = (ligneNum: number, longueur: number) => {
    const prochaineLigne = ligneNum + 1;

    if (longueur === 27 && prochaineLigne <= nbLignes) {
      form.setFocus(`ligne${prochaineLigne}` as keyof CreatePetiteAnnonceDto);
    }
  };

  const handleAjouterLigne = () => {
    const prochaineLigne = nbLignes + 1;
    setNbLignes(prochaineLigne);
    requestAnimationFrame(() => form.setFocus(`ligne${prochaineLigne}` as keyof CreatePetiteAnnonceDto));
  };

  const handleSubmit = (data: CreatePetiteAnnonceDto) => {
    createMutation.mutate(data, {
      onSuccess: async (sessionId) => {
        const stripe = await loadStripe(`${import.meta.env.VITE_STRIPE_KEY}`);
        await stripe?.redirectToCheckout({ sessionId });
        queryClient.invalidateQueries({ queryKey: [ksUserQK] });
        queryClient.invalidateQueries({ queryKey: [ksPetitesAnnoncesQK] });
      },
      onError: () => {
        toast.error("Une erreur est survenue lors de la création de l'annonce");
      },
    });
  };

  return (
    <div>
      <div className='flex flex-row gap-5 mb-6 items-center'>
        <Link to='/petites-annonces'>
          <button className='rounded-full bg-white border p-3 hover:bg-gray-50 active:bg-gray-100'>
            <ArrowLeftIcon />
          </button>
        </Link>
        <Title>Publier une petite annonce</Title>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Box className='p-3 mb-3'>
            <Title order={3} className='mb-3'>
              Vos coordonnées
            </Title>
            {!user.envoyerAdresseDifferente && (
              <div className='p-3 text-sm border rounded bg-blue-50 mb-3'>
                <div className='font-bold mb-1'>Attention</div>
                <div className='text-gray-500'>
                  Cette adresse est également l'adresse à laquelle vous recevrez la revue si vous êtes abonné(e) à
                  l'Abeille de France et que vous n'avez pas choisi explicitement de recevoir la revue à une adresse
                  différente de votre adresse de facturation dans vos coordonnées.
                </div>
              </div>
            )}
            <div className='grid grid-cols-1 md:grid-cols-12 gap-3'>
              <div className='lg:col-span-12'>
                <FormField
                  control={form.control}
                  name='personneMorale'
                  render={({ field }) => (
                    <FormItem className='flex flex-row items-center gap-4'>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <div className='space-y-0.5'>
                        <FormLabel className='text-base'>Ce compte est une société</FormLabel>
                      </div>
                    </FormItem>
                  )}
                />
              </div>
              {form.watch("personneMorale") && (
                <>
                  <div className='md:col-span-6'>
                    <MyFormField control={form.control} label='Raison sociale' name='raisonSociale' />
                  </div>
                  <div className='md:col-span-3'>
                    <MyFormField control={form.control} label='Siret' name='siret' />
                  </div>
                  <div className='md:col-span-3'>
                    <MyFormField control={form.control} label='Numéro de TVA' name='noTva' />
                  </div>
                </>
              )}

              <div className='md:col-span-4'>
                <MyFormField control={form.control} label='Nom' name='nom' />
              </div>
              <div className='md:col-span-4'>
                <MyFormField control={form.control} label='Prénom' name='prenom' />
              </div>

              <div className='md:col-span-4'>
                <MyFormField control={form.control} label='Téléphone' name='telephone' />
              </div>
              <div className='md:col-span-6'>
                <MyFormField control={form.control} label='Adresse' name='adresse1' />
              </div>
              <div className='md:col-span-6'>
                <MyFormField control={form.control} label='Complément d’adresse' name='adresse2' />
              </div>
              <div className='md:col-span-2'>
                <MyFormField control={form.control} label='Code postal' name='codePostal' />
              </div>
              <div className='md:col-span-4'>
                <MyFormField control={form.control} label='Ville' name='ville' />
              </div>
              {/* Type de pays */}
              <div className='md:col-span-3'>
                <FormField
                  control={form.control}
                  name='typePays'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pays ou territoire</FormLabel>
                      <Select
                        onValueChange={(val) => {
                          field.onChange(val);
                          if (val === TypePays.FRANCE) {
                            form.setValue("pays", "FRANCE");
                          } else {
                            form.setValue("pays", "");
                          }
                        }}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder='Sélectionner' />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={TypePays.FRANCE}>France</SelectItem>
                          <SelectItem value={TypePays.DROM_COM}>DROM-COM sauf Guyane ou Mayotte</SelectItem>
                          <SelectItem value={TypePays.MAYOTTE_GUYANNE}>Mayotte ou Guyane</SelectItem>
                          <SelectItem value={TypePays.UE}>Union Européenne</SelectItem>
                          <SelectItem value={TypePays.HORS_UE}>Hors Union Européenne</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              {/* Préciser le pays */}
              {form.watch("typePays") !== TypePays.FRANCE && (
                <div className='md:col-span-3'>
                  <MyFormField required control={form.control} label='Préciser le pays ou territoire' name='pays' />
                </div>
              )}
            </div>
          </Box>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            <Box className='p-3'>
              <Title order={3} className='mb-3'>
                Votre annonce
              </Title>
              <div className='text-sm text-gray-500 mb-3 p-2 border border-blue-300 bg-blue-50 rounded-md'>
                En soutient aux apiculteurs, les petites annonces concernant exclusivement la vente de miel et les
                proposistion ou demandes d'emplois/de stages sont provisoirement gratuites pour les abonné(e)s.
              </div>
              <div>
                <FormField
                  name='categorieId'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Catégorie</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <SelectTrigger id='categorieId' className='max-w-[450px] mt-0.5'>
                          <SelectValue placeholder='Sélectionner une catégorie' />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((categorie) => (
                            <SelectItem key={categorie.id} value={categorie.id}>
                              {categorie.nom} (
                              {categorie.isGratuit ? "0 €" : `${formatPrix(tarif.forfait / 100)} par parution`})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {categorie?.avertissement?.trim() && (
                        <div
                          role='status'
                          className='!mt-3 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm text-orange-700'
                        >
                          <TriangleAlert aria-hidden='true' className='mt-0.5 h-4 w-4 shrink-0' />
                          <p className='min-w-0 whitespace-pre-wrap break-words'>{categorie.avertissement}</p>
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className='flex items-center gap-3 mt-5 mb-4'>
                <Switch
                  id='tramage'
                  checked={form.watch("tramage")}
                  onCheckedChange={(checked) => form.setValue("tramage", checked)}
                  disabled={categorie?.isGratuit}
                />
                <div className='flex flex-col'>
                  <Label htmlFor='tramage'>Supplément tramage (+{formatPrix(tarif.tramage / 100)} par parution)</Label>
                  <p className='text-xs text-gray-400'>
                    {categorie?.isGratuit
                      ? "Non disponible pour l'offre gratuite."
                      : "Une annonce avec tramage sera mise en valeur sur un fond jaune."}
                  </p>
                </div>
              </div>

              <div className='text-sm text-gray-500 mb-3 p-2 border border-blue-300 bg-blue-50 rounded-md'>
                <div className='font-bold mb-1'>Important</div>
                Pensez à indiquer votre <span className='font-semibold'>numéro de téléphone</span> dans le texte de
                l'annonce ainsi que votre
                <span className='font-semibold'> département</span> s'il concerne un département différent de votre
                adresse postale.
              </div>

              <div className='space-y-3' ref={parent}>
                <MyFormField
                  control={form.control}
                  label='Ligne 1'
                  className='max-w-[450px]'
                  name='ligne1'
                  description={`${form.watch("ligne1").length}/27 caractères`}
                  maxLength={27}
                  onInput={(event) => handleLigneComplete(1, event.currentTarget.value.length)}
                />
                <MyFormField
                  control={form.control}
                  label='Ligne 2'
                  name='ligne2'
                  className='max-w-[450px]'
                  maxLength={27}
                  description={`${form.watch("ligne2").length}/27 caractères`}
                  onInput={(event) => handleLigneComplete(2, event.currentTarget.value.length)}
                />
                <MyFormField
                  control={form.control}
                  label='Ligne 3'
                  name='ligne3'
                  maxLength={27}
                  className='max-w-[450px]'
                  description={`${form.watch("ligne3").length}/27 caractères`}
                  onInput={(event) => handleLigneComplete(3, event.currentTarget.value.length)}
                />
                <MyFormField
                  control={form.control}
                  label='Ligne 4'
                  className='max-w-[450px]'
                  name='ligne4'
                  maxLength={27}
                  description={`${form.watch("ligne4").length}/27 caractères`}
                  onInput={(event) => handleLigneComplete(4, event.currentTarget.value.length)}
                />
                {Array.from({ length: 6 }, (_, i) => i + 5).map((ligneNum) => (
                  <LigneSupplementaire
                    key={ligneNum}
                    ligneNum={ligneNum}
                    nbLignes={nbLignes}
                    setNbLignes={setNbLignes}
                    form={form}
                    categorie={categorie}
                    onLigneComplete={handleLigneComplete}
                  />
                ))}
                <Button
                  type='button'
                  disabled={nbLignes >= 10 || categorie?.isGratuit}
                  variant='outline'
                  size='sm'
                  onClick={handleAjouterLigne}
                >
                  Ajouter une ligne (+{formatPrix(tarif.ligneSupp / 100)} par parution)
                </Button>
              </div>
              {categorie?.isGratuit && (
                <div className='text-sm mt-4 text-gray-500 mb-3 p-2 border border-blue-300 bg-blue-50 rounded-md'>
                  <div className='font-bold mb-1'>Important</div>
                  Vous ne pouvez pas ajouter de ligne supplémentaire car vous bénéficiez de l'offre gratuite.
                </div>
              )}
            </Box>
            {/* Mois de parution */}
            <Box className='p-3 max-h-max'>
              <Title order={3} className='mb-4'>
                Mois de parution souhaités
              </Title>
              {form.formState.errors.mois && (
                <p className='text-red-500 text-sm'>{form.formState.errors.mois.message}</p>
              )}
              <div className='flex flex-col mt-5 gap-3'>
                {moisDeParution.map((pickedMois) => (
                  <div key={pickedMois} className='flex flex-row gap-3 items-center'>
                    <Switch
                      id={pickedMois}
                      checked={form.watch("mois").includes(pickedMois)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          if (form.formState.errors.mois) {
                            form.clearErrors("mois");
                          }
                          form.setValue("mois", [...form.watch("mois"), pickedMois]);
                        } else {
                          form.setValue(
                            "mois",
                            form.watch("mois").filter((m) => m !== pickedMois)
                          );
                        }
                      }}
                    />
                    <Label htmlFor={pickedMois}>{new Date(pickedMois).getMonth() === 6 ? `Juillet/août ${dayjs(pickedMois).year()}` : dayjs(pickedMois).format("MMMM YYYY")}</Label>
                  </div>
                ))}
              </div>
            </Box>
          </div>

          <Box className='mt-4 max-w-[450px]'>
            <Title order={3} className='mb-4'>
              Panier et paiement
            </Title>
            <div className='mb-3'>
              <Label className='mb-0.5'>Mode de paiement</Label>
              <Select value='cb'>
                <SelectTrigger>
                  <SelectValue placeholder='Sélectionner une option' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='cb'>Carte bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='flex flex-row gap-3 mt-5'>
              <div className='flex flex-col'>
                <Label>Montant total</Label>
                <p className={clsx("text-lg font-bold", isCalculatingPrix && "text-gray-400")}>
                  {isCalculatingPrix
                    ? "Calcul du prix..."
                    : formatPrix(prixData?.montantTtc ? prixData.montantTtc / 100 : 0)}
                </p>
                {prixData && prixData.tauxReduction > 0 && (
                  <p className='text-sm text-gray-500 italic'>Réduction de {prixData.tauxReduction * 100}% appliquée</p>
                )}
              </div>
            </div>
          </Box>

          <div className='flex flex-row gap-3 items-center mt-4'>
            <Checkbox id='acceptConditions' required />
            <Label htmlFor='acceptConditions'>
              Je confirme avoir indiqué mon numéro de téléphone dans le texte de l'annonce ainsi que mon département
              s'il concerne un département différent de mon adresse postale.
            </Label>
          </div>

          {Object.keys(form.formState.errors).length > 0 && (
            <div className='text-red-500 mt-4 text-sm p-2 rounded-md border border-red-400 bg-red-50'>
              {form.formState.errors.categorieId?.message || "Des informations sont manquantes dans le formulaire"}
            </div>
          )}

          <div className='flex flex-col md:flex-row gap-3 mt-7 mb-4'>
            <Button
              type='submit'
              disabled={createMutation.isPending || isCalculatingPrix}
              loading={createMutation.isPending}
            >
              {isCalculatingPrix
                ? "Calcul du prix..."
                : prixData && prixData.montantTtc > 0
                  ? `Valider et payer ${formatPrix(prixData.montantTtc ? prixData.montantTtc / 100 : 0)}`
                  : "Valider et publier"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
